import { useEffect, useRef, useState } from 'react';
import useBgPages from '../../hooks/use-bg-pages.hook';
import { useTranslation } from 'react-i18next';
import useRequest from '../../hooks/use-request.hook';
import Aside from '../../components/layout/asaid.component';
import Header from '../../components/layout/header.component';
import Footer from '../../components/layout/footer.component';
import DataTable from '../../components/datatable/datatable.component';
import { RolesService } from '../../services/roles.service';
import { AlertService } from '../../services/alert.service';
import { PROFILES } from '../../constants';
import AddRoles from './partial/add-roles.partial';
import ActionsDropdown from '../../components/datatable/actions-dropdown.component';
import DuplicateRoles from './modal/duplicate-roles.modal';
import Icon from '../../components/forms/icon.component';
import PermissionsView from './modal/permissions-view.modal';
import LogData from '../../components/logs/log-data.component';
import DateRegister from '../../components/datatable/register-data.component';
import { useNavigate, useLocation } from 'react-router-dom';
import PrincipalCard from '../../components/cards/principal-card.component';
import { useSession } from '../../hooks/use-session.hook';

function RolesPage() {
  useBgPages();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { session, selectedAccount } = useSession();
  const { call, isRequesting } = useRequest();
  const [rows, setRows] = useState<any>([]);
  const [total, setTotal] = useState<any>();
  const [actives, setActives] = useState<number>();
  const addRolesRef = useRef<any>(null);
  const profilesLabel = {
    [PROFILES.ROOT]: t('roles.rol-root'),
    [PROFILES.STANDARD_USER]: t('roles.rol-standard-user')
  };

  const dataStatistics = [
    { label: t('roles.my-roles'), total: total, type: '', icon: 'ki-book-open' },
    { label: t('channels.wa.actives'), total: actives, type: 'primary', icon: 'ki-check-circle' }
  ];
  const columns = [
    {
      name: t('roles.roles-data'),
      filterColumn: true,
      sortable: true,
      selector: (r: any) => `${r.name} ${r.account} ${r.company}`,
      width: 25,
      cell: (rows: any) => {
        return (
          <div className="text-nowrap">
            <p className="mb-1">
              <strong className="fs-6">{t('roles.title-column-1')}:</strong>
              {' ' + rows.name}
            </p>
            {session.profile === PROFILES.ROOT && rows.account && rows.account.company && (
              <>
                <p className="mb-1">
                  <strong className="fs-6">{t('roles.company-label')}:</strong>
                  {' ' + rows.account.company.name}
                </p>
                <p className="">
                  <strong className="fs-6">{t('roles.account-label')}: </strong>
                  {' ' + rows.account.name}
                </p>
              </>
            )}
          </div>
        );
      }
    },
    {
      name: t('roles.profile-label'),
      sortable: true,
      selector: (r: any) => `${r.profile}`,
      width: 20,
      cell: (rows: any) => {
        return (
          <div className="mw-200px text-nowrap fs-6">
            <strong className="m-0">
              {' ' + (profilesLabel[rows.profile.toUpperCase()] || rows.profile)}
            </strong>
          </div>
        );
      }
    },
    {
      name: t('users-page.title-column-4'),
      sortable: true,
      selector: (r: any) => `${r.created_at}`,
      width: 20,
      cell: (row: any) => {
        return <DateRegister created_at={row.created_at} />;
      }
    },
    {
      name: t('roles.title-column-3'),
      sortable: true,
      width: 13,
      selector: (r: any) => `${r.status}`,
      cell: (row: any) =>
        row.status ? (
          <span className="badge text-dark fs-5">
            <Icon icon="ki-check-circle" size="fs-3" type="primary" />
            {t('general.active')}
          </span>
        ) : (
          <span className="badge text-dark fs-5">
            <Icon icon="ki-minus-circle" size="fs-3" type="danger" />
            {t('general.inactive')}
          </span>
        )
    },
    {
      name: t('roles.permissions-title'),
      sortable: true,
      selector: (r: any) => `${r._id}`,
      cell: (rows: any) => {
        return (
          <div className="mw-200px">
            <button
              type="button"
              className="btn btn-light btn-sm hover-scale fw-bolder btn-center"
              onClick={() => setShowModalPermissions({ show: true, data: rows })}
            >
              <Icon icon="ki-eye" size="fs-2" />
              {t('roles.watch')}
            </button>
          </div>
        );
      }
    },
    {
      name: t('general.acctions'),
      sortable: true,
      center: true,
      selector: (r: any) => `${r._id}`,
      exportable: false,
      cell: (rows: any) => {
        const options =
          rows.profile === PROFILES.ROOT
            ? []
            : session.profile === PROFILES.ROOT
              ? [
                  {
                    label: t('roles.duplicate-role'),
                    icon: 'ki-book-open',
                    color: 'info',
                    onClick: () => setShowModalSecondary({ show: true, data: rows })
                  }
                ]
              : [
                  {
                    label: t('roles.duplicate-role'),
                    icon: 'ki-book-open',
                    color: 'info',
                    onClick: () => DuplicateAccountAdmin(rows, 'duplicateRole-9189283022')
                  }
                ];

        return (
          <ActionsDropdown
            edit={() => {
              setShowModal({ show: true, data: rows });
              navigate('/roles/builder-role', { state: { payload: rows } });
            }}
            changeStatus={() => changeStatus(rows._id, 'changeStatus-7394734567', rows.status)}
            rowStatus={rows.status}
            // options={options}
          />
        );
      }
    }
  ];

  const DuplicateAccountAdmin = async (rows: any, buttonId: any) => {
    const data = {
      role: rows._id,
      account: rows.account._id
    };
    const isConfirmed = await AlertService.question(
      t('general.atention'),
      `${t('¿Estas seguro de duplicar')} <b>${rows.name}?</b> <br/><br/> Se duplicara en tu cuenta actual `,
      null as any,
      'Duplicar'
    );
    if (!isConfirmed) return null;
    try {
      await RolesService.duplicateRoles(data);
      getRoles();
      AlertService.toastSuccess(t('roles.role-duplicate-success'));
      LogData(data, buttonId, window.location.href);
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  const changeStatus = async (id: string, buttonId: string, status: boolean) => {
    const isConfirmed = await AlertService.warning(
      t('general.atention'),
      t('roles.change-status-warning'),
      () => Promise.resolve(true)
    );
    if (!isConfirmed) return;
    try {
      await RolesService.changeStatus({ role: id }, status ? 'disable' : 'enable');
      AlertService.toastSuccess(t('roles.change-status-success'));
      await getRoles();
      LogData({ user: id, type: status ? 'disable' : 'enable' }, buttonId, window.location.href);
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  const getRoles = async () => {
    try {
      let data = [];
      if (session.profile === PROFILES.ROOT) {
        data = await call(() => RolesService.getRoles());
        if (data) {
          setRows(data);
          setTotal(data?.length);
          const activeRoles = data?.filter((roles: any) => roles.status === true);
          setActives(activeRoles.length);
        } else {
          setRows([]);
          setTotal(0);
          setActives(0);
        }
      } else if (session.profile === PROFILES.ADMIN) {
        data = await call(() => RolesService.getAccountRoles(selectedAccount.account_id));
        if (data) {
          setRows(data);
          setTotal(data?.length);
          const activeRoles = data?.filter((roles: any) => roles.status === true);
          setActives(activeRoles.length);
        } else {
          setRows([]);
          setTotal(0);
          setActives(0);
        }
      }
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  const [showModal, setShowModal] = useState({
    show: false,
    data: null
  });

  const handleShowModal = async (refresh: boolean) => {
    if (refresh) {
      getRoles();
    }
    setShowModal({ show: false, data: null });
  };

  const [showModalSecondary, setShowModalSecondary] = useState({
    show: false,
    data: null
  });

  const handleShowModalSecondary = async (refresh: boolean) => {
    if (refresh) {
      getRoles();
    }
    setShowModalSecondary({ show: false, data: null });
  };

  const [showModalPermissions, setShowModalPermissions] = useState({
    show: false,
    data: null
  });

  const handleShowModaPermissions = async (refresh: boolean) => {
    if (refresh) {
      getRoles();
    }
    setShowModalPermissions({ show: false, data: null });
  };

  useEffect(() => {
    getRoles();
  }, []);

  useEffect(() => {
    if (location.pathname.includes('/roles/builder-role')) {
      setShowModal((prev) => ({
        show: true,
        data: prev.data ?? showModal.data
      }));
    } else {
      setShowModal({ show: false, data: null });
    }
  }, [location.pathname]);

  return (
    <>
      <div className="page d-flex flex-row flex-column-fluid">
        <Aside />
        <div className="wrapper d-flex flex-column flex-row-fluid" id="kt_wrapper">
          <Header />
          <div className="content fs-6 d-flex flex-column flex-column-fluid" id="kt_content">
            <div className="post fs-6 d-flex flex-column-fluid" id="kt_post">
              <div className="container-xxl">
                {showModal.show || location.pathname.includes('/roles/builder-role') ? (
                  <AddRoles
                    ref={addRolesRef}
                    show={true}
                    onRequestClose={(refresh: any) => handleShowModal(refresh)}
                  />
                ) : (
                  <>
                    <PrincipalCard
                      dataStatistics={dataStatistics}
                      btnCreate
                      onClick={() => {
                        setShowModal({ show: true, data: null });
                        navigate('/roles/builder-role');
                      }}
                      btnRefresh
                      onRefresh={async () => {
                        await getRoles();
                        AlertService.toastSuccess('¡ Éxito !, información actualizada.');
                      }}
                      title={t('general.register')}
                      titleBolder={t('asaid.link-roles')}
                      subtitle={t('roles.page-text')}
                      breadcrumbItems={[t('asaid.link-roles')]}
                      textButton={t('roles.add-roles')}
                      iconButton="ki-soft-2"
                    />
                    <div className="row g-xl-12">
                      <div className="col-xl-12 mb-5">
                        <div className="card">
                          <div className="card-header align-items-center border-0 mt-5">
                            <div className="card-title align-items-start flex-column animate-fadeInUp animate-delay-1 h3">
                              <span className="fw-bolder fw-bold fs-2">
                                {t('asaid.link-roles')}
                              </span>
                              <span className="text-gray-700 mt-2 fw-semibold fs-6">
                                {t('roles.registered')}
                              </span>
                            </div>
                          </div>
                          <div className="card-body pt-1">
                            <DataTable
                              columns={columns}
                              data={rows}
                              isLoading={isRequesting}
                              order={{ columnIndex: 0, direction: 'desc' }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
      <DuplicateRoles
        show={showModalSecondary.show}
        onRequestClose={handleShowModalSecondary}
        payload={showModalSecondary.data}
      />
      <PermissionsView
        show={showModalPermissions.show}
        onRequestClose={handleShowModaPermissions}
        payload={showModalPermissions.data}
      />
    </>
  );
}

export default RolesPage;
